// chartHeadline — the big figure on a shared chart card.
//
// ⚠️ IT IS DERIVED, NOT DECORATIVE, SO IT REFUSES MORE OFTEN THAN IT SPEAKS.
// This number sits in 38px type on an image people forward; if it is ever the
// wrong number, or the right number labelled as the wrong thing, the card is
// worse than no card. Every case where "the latest value and its change" is
// ambiguous returns null and the band simply carries the title.
//
// Refused deliberately:
//   • more than one series  — which line is the headline? Nothing says.
//   • a category axis       — "day over day" is meaningless across buckets.
//   • bars                  — same reason.
//   • fewer than two points — there is no change to state.

import type { ChartSeriesPoint } from "@/types/consultant";
import type { CardSeries } from "./cardChart";

export interface ChartHeadline {
  /** Latest value, unformatted. */
  value: number;
  /** Change against the previous point. Null when it cannot be computed. */
  delta: number | null;
}

/**
 * The points the card may draw — and the points the headline is read from.
 *
 * ⚠️ ONE GATE FOR BOTH, DELIBERATELY. The card draws this series and states a
 * figure over it; if the two were derived separately they could disagree, and a
 * headline that does not match the line beneath it is the worst thing this card
 * could produce. They pass or fail together.
 */
export function chartCardPoints(
  series: ChartSeriesPoint[] | undefined,
  opts: { chartType?: string; xAxis?: string } = {},
): { date: string; value: number }[] | null {
  if (opts.chartType && opts.chartType !== "line") return null;
  if (opts.xAxis && opts.xAxis !== "date") return null;
  if (!Array.isArray(series) || series.length < 2) return null;

  // ⚠️ ONE LINE ONLY. A card headlining "$375.82" over a two-line chart is
  // stating a number for a line the reader cannot identify.
  const names = new Set(series.map((p) => p.series_name ?? "").filter(Boolean));
  if (names.size > 1) return null;

  const usable = series
    .filter((p) => typeof p.value === "number" && Number.isFinite(p.value) && p.date)
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((p) => ({ date: String(p.date), value: p.value as number }));
  return usable.length < 2 ? null : usable;
}

export function chartHeadline(
  series: ChartSeriesPoint[] | undefined,
  opts: { chartType?: string; xAxis?: string } = {},
): ChartHeadline | null {
  const usable = chartCardPoints(series, opts);
  if (!usable) return null;

  const latest = usable[usable.length - 1];
  const prior = usable[usable.length - 2];
  return { value: latest.value, delta: latest.value - prior.value };
}

/** "$375.82" / "375.82" — currency unless the axis says otherwise. */
export function formatHeadlineValue(v: number, format?: string): string {
  if (format === "integer") return Math.round(v).toLocaleString();
  return `$${v.toFixed(2)}`;
}

/** "▼ $0.41 on the day" — direction in the glyph AND the sign. */
export function formatHeadlineDelta(delta: number, format?: string): string {
  const mark = delta > 0 ? "▲" : delta < 0 ? "▼" : "—";
  const body =
    format === "integer"
      ? Math.abs(Math.round(delta)).toLocaleString()
      : `$${Math.abs(delta).toFixed(2)}`;
  if (delta === 0) return `${mark} unchanged on the day`;
  return `${mark} ${body} on the day`;
}

/**
 * The whole chart, in the form the card draws.
 *
 * ⚠️ THIS IS WHERE THE CARD REFUSES, AND IT REFUSES BY SHAPE RATHER THAN BY
 * GUESSWORK. A payload it cannot read as "lines over dates" or "bars over
 * categories" returns null and is photographed instead — the old path, faults
 * and all, but never a redrawn chart that misstates its own data.
 *
 * Refused deliberately:
 *   • chart_type "none"        — there is no chart.
 *   • bars with several series — grouped bars need an axis of group labels and
 *                                a legend the card has no room for, and the
 *                                consultant does not emit that shape.
 *   • more than four lines     — the legend stops being readable, and four is
 *                                already more than any current template emits.
 *   • any line under two points — a lone dot drawn as a trend.
 */
export function chartCardSeries(
  series: ChartSeriesPoint[] | undefined,
  opts: { chartType?: string; xAxis?: string } = {},
): { series: CardSeries[]; bars: boolean } | null {
  if (!Array.isArray(series) || !series.length) return null;
  const type = opts.chartType;
  if (type !== "line" && type !== "bar") return null;

  const finite = series.filter(
    (p) => typeof p.value === "number" && Number.isFinite(p.value),
  );
  if (!finite.length) return null;

  if (type === "bar") {
    if (opts.xAxis !== "category") return null;
    const names = new Set(finite.map((p) => p.series_name ?? "").filter(Boolean));
    if (names.size > 1) return null;
    const pts = finite
      .map((p) => ({
        date: String((p as { label?: string }).label ?? p.date ?? ""),
        value: p.value as number,
      }))
      .filter((p) => p.date);
    return pts.length ? { series: [{ name: "", points: pts }], bars: true } : null;
  }

  if (opts.xAxis !== "date") return null;
  const groups = new Map<string, { date: string; value: number }[]>();
  for (const p of finite) {
    if (!p.date) continue;
    const key = p.series_name ?? "";
    const arr = groups.get(key) ?? [];
    arr.push({ date: String(p.date), value: p.value as number });
    groups.set(key, arr);
  }
  const out: CardSeries[] = [];
  for (const [name, pts] of groups) {
    pts.sort((a, b) => a.date.localeCompare(b.date));
    if (pts.length >= 2) out.push({ name, points: pts });
  }
  if (!out.length || out.length > 4) return null;
  return { series: out, bars: false };
}
